import {
  API_PREFIX,
  type Alert,
  type AlertRule,
  type LogLine,
  type MetricSample,
  type MetricsHistory,
  type ProcessDetail,
  type ProcessSummary,
  type SystemInfo,
} from '@pm2-monitor/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Aborted rather than left hanging when the bridge or the tunnel stalls. */
const REQUEST_TIMEOUT_MS = 12_000;

async function request<T>(
  token: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      // Fastify rejects an empty body when content-type is application/json,
      // so it is only declared on requests that actually carry one.
      ...(init.body != null ? { 'content-type': 'application/json' } : {}),
    },
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  if (!response.ok) {
    let message = `request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Non-JSON error body; the status-derived message stands.
    }
    throw new ApiError(message, response.status);
  }

  return (await response.json()) as T;
}

/**
 * Read paths plus the two writes the design exposes. Lifecycle actions
 * (restart, stop, delete, start) are deliberately absent: the console is
 * read-only, and those stay in the phone app where they are confirmed per
 * action. Adding them here would mean shipping the ability to take production
 * down to anything that can read a token out of a browser tab.
 */
export const api = {
  system: (t: string) => request<SystemInfo>(t, '/system'),

  processes: (t: string) => request<{ processes: ProcessSummary[] }>(t, '/processes'),

  process: (t: string, id: number) =>
    request<{ process: ProcessDetail; series: MetricSample[] }>(t, `/processes/${id}`),

  processLogs: (
    t: string,
    id: number,
    options: {
      lines?: number;
      stream?: 'out' | 'err';
      source?: 'live' | 'file';
    } = {},
  ) => {
    const query = new URLSearchParams({
      lines: String(options.lines ?? 200),
      stream: options.stream ?? 'out',
      source: options.source ?? 'file',
    });
    return request<{ lines: LogLine[] }>(t, `/processes/${id}/logs?${query}`);
  },

  recentLogs: (t: string, lines = 200) =>
    request<{ lines: LogLine[] }>(t, `/logs?lines=${lines}`),

  metricsHistory: (t: string) => request<MetricsHistory>(t, '/metrics/history'),

  alerts: (t: string) => request<{ alerts: Alert[]; rules: AlertRule[] }>(t, '/alerts'),

  acknowledgeAlert: (t: string, id: string) =>
    request<{ ok: true }>(t, `/alerts/${id}/acknowledge`, { method: 'POST' }),

  updateRule: (t: string, id: string, patch: Partial<AlertRule>) =>
    request<{ rule: AlertRule }>(t, `/alerts/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
};
